import { Injectable, computed, inject, signal } from '@angular/core';
import { tap } from 'rxjs';

import {
  CommissionLedgerEntry,
  PartnerClient,
  PartnerDashboard,
  PartnerProfile,
  PartnerSettings,
  PayoutRecord,
  ReferralInvite,
  ReferralLink
} from '@app/core/models';
import {
  PartnerApiService,
  PartnerClientUpsertPayload,
  PartnerInviteActionCommand,
  PartnerInviteCommand,
  PartnerReferralLinkPayload,
  PartnerOwnerInvitationPayload,
  PartnerManagedOwnerPayload
} from './partner-api.service';

@Injectable({ providedIn: 'root' })
export class PartnerStateService {
  private readonly api = inject(PartnerApiService);

  private readonly dashboardState = signal<PartnerDashboard | null>(null);
  private readonly profileState = signal<PartnerProfile | null>(null);
  private readonly settingsState = signal<PartnerSettings | null>(null);
  private readonly clientsState = signal<PartnerClient[]>([]);
  private readonly invitesState = signal<ReferralInvite[]>([]);
  private readonly referralLinksState = signal<ReferralLink[]>([]);
  private readonly commissionsState = signal<CommissionLedgerEntry[]>([]);
  private readonly payoutsState = signal<PayoutRecord[]>([]);

  readonly dashboard = computed(() => this.dashboardState());
  readonly profile = computed(() => this.profileState());
  readonly settings = computed(() => this.settingsState());
  readonly clients = computed(() => this.clientsState());
  readonly invites = computed(() => this.invitesState());
  readonly referralLinks = computed(() => this.referralLinksState());
  readonly commissions = computed(() => this.commissionsState());
  readonly payouts = computed(() => this.payoutsState());

  loadDashboard(startDate?: string, endDate?: string) {
    return this.api.getDashboard(startDate, endDate).pipe(tap((dashboard) => this.dashboardState.set(dashboard)));
  }

  loadProfile() {
    return this.api.getProfile().pipe(tap((profile) => this.profileState.set(profile)));
  }

  updateProfile(payload: Partial<PartnerProfile>) {
    return this.api.updateProfile(payload).pipe(tap((profile) => this.profileState.set(profile)));
  }

  loadSettings() {
    return this.api.getSettings().pipe(tap((settings) => this.settingsState.set(settings)));
  }

  updateSettings(payload: Partial<PartnerSettings>) {
    return this.api.updateSettings(payload).pipe(tap((settings) => this.settingsState.set(settings)));
  }

  removeAchBankAccount() {
    return this.api.removeAchBankAccount().pipe(tap((settings) => this.settingsState.set(settings)));
  }

  loadClients() {
    return this.api.getClients().pipe(tap((clients) => this.clientsState.set(clients)));
  }

  createClient(payload: PartnerClientUpsertPayload) {
    return this.api.createClient(payload).pipe(
      tap((client) => this.clientsState.update((clients) => [client, ...clients]))
    );
  }

  updateClient(partnerClientId: string, payload: PartnerClientUpsertPayload) {
    return this.api.updateClient(partnerClientId, payload).pipe(
      tap((client) =>
        this.clientsState.update((clients) =>
          clients.map((entry) => (entry.id === partnerClientId ? client : entry))
        )
      )
    );
  }

  loadInvites() {
    return this.api.getInvites().pipe(tap((invites) => this.invitesState.set(invites)));
  }

  createInvite(payload: PartnerInviteCommand) {
    return this.api.createInvite(payload).pipe(
      tap((invite) => this.invitesState.update((invites) => [invite, ...invites]))
    );
  }

  updateInvite(payload: PartnerInviteActionCommand) {
    return this.api.updateInvite(payload).pipe(
      tap((invite) =>
        this.invitesState.update((invites) =>
          invites.map((entry) => (entry.id === invite.id ? invite : entry))
        )
      )
    );
  }

  loadReferralLinks() {
    return this.api
      .getReferralLinks()
      .pipe(tap((referralLinks) => this.referralLinksState.set(referralLinks)));
  }

  createReferralLink(payload: PartnerReferralLinkPayload) {
    return this.api.createReferralLink(payload).pipe(
      tap((referralLink) =>
        this.referralLinksState.update((referralLinks) => [referralLink, ...referralLinks])
      )
    );
  }

  loadCommissions() {
    return this.api
      .getCommissions()
      .pipe(tap((commissions) => this.commissionsState.set(commissions)));
  }

  loadPayouts() {
    return this.api.getPayouts().pipe(tap((payouts) => this.payoutsState.set(payouts)));
  }

  requestPayout(amount?: number) {
    return this.api.requestPayout(amount).pipe(
      tap((payout) => this.payoutsState.update((records) => [payout, ...records]))
    );
  }

  createOwnerInvitation(payload: PartnerOwnerInvitationPayload, idempotencyKey: string) {
    return this.api.createOwnerInvitation(payload, idempotencyKey).pipe(
      tap(() => this.loadClients().subscribe())
    );
  }

  createManagedOwner(payload: PartnerManagedOwnerPayload, idempotencyKey: string) {
    return this.api.createManagedOwner(payload, idempotencyKey).pipe(
      tap(() => this.loadClients().subscribe())
    );
  }

  completeManagedOwnerRegistration(partnerClientId: string, idempotencyKey: string) {
    return this.api.completeManagedOwnerRegistration(partnerClientId, idempotencyKey).pipe(
      tap(() => this.loadClients().subscribe())
    );
  }

  resendOwnerInvitation(invitationId: string, idempotencyKey: string) {
    return this.api.resendOwnerInvitation(invitationId, idempotencyKey);
  }

  revokeOwnerInvitation(invitationId: string, idempotencyKey: string) {
    return this.api.revokeOwnerInvitation(invitationId, idempotencyKey);
  }
}
